<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import Modal from '@shared/components/Modal.vue';
    import Loading from '@shared/components/Loading.vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import { formatDateTime } from '@shared/utils/formatDateTime';
    import { loadParticipantHistory, loadSurveyResponse } from './EventGuestList.access';
    import { type GuestRow } from './guest-list-types';
    import { type CompleteSurveyResponse, type ParticipantHistoryEntry, type ResponseQuestion } from './guest-response-types';
    import { QuestionType } from '@views/Surveys/SurveysTypes.d';
    import { layoutResponseQuestions } from '@views/Surveys/SurveyStatistics/SurveyStatistics.utils';
    import { useSurveyResourcePreviews } from '@views/Surveys/SurveyStatistics/use-survey-resource-previews';
    import { downloadAllSurveyResources } from '@views/Surveys/SurveyStatistics/SurveyStatistics.access';
    import GuestResponseQuestion from './GuestResponseQuestion.vue';

    // Port of the Knockout _SurveyResponseModal + survey-response component.

    const props = defineProps<{
        row: GuestRow | null;
        eventEnded: boolean;
    }>();

    const emit = defineEmits<{
        close: [edited: boolean];
        showContact: [contactId: string];
    }>();

    const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

    const show = ref(false);
    const loading = ref(false);
    const response = ref<CompleteSurveyResponse | null>(null);
    const history = ref<ParticipantHistoryEntry[]>([]);
    const edited = ref(false);

    const { resourcePreviewUrls, clearResourcePreviewUrls, loadResourcePreviews } = useSurveyResourcePreviews();

    const participant = computed(() => props.row?.participant ?? null);
    const contact = computed(() => response.value?.Contact ?? null);
    const hasContact = computed(() => !!contact.value && contact.value.ContactId !== EMPTY_GUID);

    async function load() {
        const surveyResponseId = participant.value?.SurveyResponseId;
        if (!participant.value || !surveyResponseId) return;

        loading.value = true;
        clearResourcePreviewUrls();
        try {
            [response.value, history.value] = await Promise.all([
                loadSurveyResponse(surveyResponseId),
                loadParticipantHistory(participant.value.EventParticipantId),
            ]);
            loadResourcePreviews(response.value.SurveyAnswers.flatMap((answer) => answer.SurveyAnswerResources ?? []));
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeading });
        } finally {
            loading.value = false;
        }
    }

    watch(
        () => props.row,
        (row) => {
            if (!row) {
                show.value = false;
                return;
            }
            response.value = null;
            history.value = [];
            edited.value = false;
            show.value = true;
            load();
        }
    );

    function onSaved() {
        edited.value = true;
        load();
    }

    function showContact() {
        if (contact.value) emit('showContact', contact.value.ContactId);
    }

    const HIDDEN_TYPES = [QuestionType.Content, QuestionType.Pagebreak, QuestionType.Divider];

    // Primary guests (with a contact) see every question except the accompanying-guests block;
    // accompanying guests see only the accompanying-guests (EventParticipant) block.
    const isVisible = (question: ResponseQuestion) =>
        !question.IsDeleted && !HIDDEN_TYPES.includes(question.Type) && !!participant.value?.ContactId !== (question.Type === QuestionType.EventParticipant);

    const visibleQuestions = computed(() => layoutResponseQuestions((response.value?.Questions ?? []).filter(isVisible)));

    const atleastOneResource = computed(() => !!response.value?.SurveyAnswers.some((a) => (a.SurveyAnswerResources?.length ?? 0) > 0));

    const contactName = computed(() => {
        const c = contact.value;
        if (!c) return '';
        return [c.FirstName, c.LastName].filter(Boolean).join(' ') || c.Email || $Resources.YourContactLabel;
    });

    const answerFor = (questionId: string) => response.value?.SurveyAnswers.find((a) => a.Question.QuestionId === questionId) ?? null;

    const title = computed(() => {
        if (!response.value) return '';
        return `${$Resources.HereIsContactsResponseFirstPartLabel} ${response.value.Contact.FirstName || $Resources.YourContactLabel}${$Resources.HereIsContactsResponseSecondPartLabel} ${response.value.Survey.Title || $Resources.NoTitlePlaceholder}`;
    });
</script>

<template>
    <Modal :show="show" size="lg" :title="title" @close="show = false" @doneClosing="emit('close', edited)">
        <template #content>
            <Loading :show="loading && !response" loadingStyle="progress-bar" />

            <template v-if="response && participant">
                <ul class="list-unstyled d-flex gap-lg flex-wrap m-lg-b">
                    <li v-if="!hasContact">
                        <small>{{ $Resources.ResponseRespondent }}:</small>
                        <span class="fw-600 d-block">{{ $Resources.AnonymousNameLabel }}</span>
                    </li>
                    <li v-else>
                        <small>{{ $Resources.ResponseRespondent }}:</small>
                        <a href="javascript:void(0)" class="fw-600 d-block" @click="showContact">{{ contactName }}</a>
                    </li>
                    <li>
                        <small>{{ $Resources.ResponseCompleted }}:</small>
                        <time class="fw-600 d-block" :datetime="response.Created">{{ formatDateTime(response.Created) }}</time>
                    </li>
                </ul>

                <div class="form-group m-lg-b" v-if="atleastOneResource">
                    <button type="button" class="btn btn-default btn-sm" @click="downloadAllSurveyResources(response.SurveyResponseId)">
                        {{ $Resources.DownloadAllResponseFiles }}
                    </button>
                </div>

                <template v-for="entry in visibleQuestions" :key="entry.question.QuestionId">
                    <h3 v-if="entry.isMatrixHeader" class="h4 m-0-t matrix-group-header">
                        {{ entry.label }} {{ entry.question.Title }}
                        <small class="fw-600">{{ $Resources.QuestionTypeMatrixLabel }}</small>
                        <small v-if="entry.question.Required">* {{ $Resources.QuestionRequiredLabel }}</small>
                    </h3>

                    <GuestResponseQuestion
                        v-else
                        :class="{
                            'matrix-child': entry.isMatrixChild,
                            'matrix-child-first': entry.isFirstMatrixChild,
                            'matrix-child-last': entry.isLastMatrixChild,
                        }"
                        :question="entry.question"
                        :answer="answerFor(entry.question.QuestionId)"
                        :label="entry.label"
                        :history="history"
                        :contact="response.Contact"
                        :surveyResponseId="response.SurveyResponseId"
                        :eventParticipantId="participant.EventParticipantId"
                        :isContactRow="!!participant.ContactId"
                        :participantSqiid="row?.data.Sqiid"
                        :eventEnded="eventEnded"
                        @saved="onSaved"
                        @showContact="showContact"
                        :resourcePreviewUrls="resourcePreviewUrls" />
                </template>
            </template>
        </template>
    </Modal>
</template>

<style lang="less" src="../../../Surveys/SurveyStatistics/question-card.less"></style>

